import { InfoCircleOutlined } from '@ant-design/icons';
import { Card, DatePicker, Tooltip } from 'antd';
import moment, { Moment } from 'moment';
import { ErrorAwareLabel, TimezoneSelect } from '../..';
import type { PromotionFormProvince } from '../../../stores/promotion';
import { BusinessUnitsSelectTree } from '../../region-selectors/business-units-select-tree';
import { useHandleFormSelectionChange } from '../../region-selectors/business-units-select-tree/hooks/use-handle-form-selection-change';
import style from './style.module.scss';

interface CommonScheduleSectionProps {
    startDate: Moment | null;
    endDate: Moment | null;
    timezone: string;
    timezoneField: string; // The field name for timezone in the form
    provinces: PromotionFormProvince[];
    setFieldValue: (field: string, value: unknown) => void;
    checkIfHasError: (path: string) => boolean;
    fileSelected?: boolean;
}

export const CommonScheduleSection = ({
    startDate,
    endDate,
    timezone,
    timezoneField,
    provinces,
    setFieldValue,
    checkIfHasError,
    fileSelected = false,
}: CommonScheduleSectionProps) => {
    const { handleProvinceSelectionChange } = useHandleFormSelectionChange();

    const renderDatePicker = (
        label: string,
        field: 'startDate' | 'endDate',
        tooltip: string,
        value: Moment | null,
        onChange: (date: Moment | null) => void,
        disabledDate: (date: Moment | null) => boolean,
        allowClear = true
    ) => (
        <div className={style.date_picker_column}>
            <div>
                <ErrorAwareLabel
                    text={label}
                    hasError={checkIfHasError(field)}
                />
                <Tooltip title={tooltip}>
                    <InfoCircleOutlined style={{ paddingLeft: '0.4rem' }} />
                </Tooltip>
            </div>
            <DatePicker
                allowClear={allowClear}
                format="YYYY-MM-DD hh:mm A"
                showTime={{ format: 'HH:mm', minuteStep: 15 }}
                value={value}
                onChange={onChange}
                disabledDate={disabledDate}
                size="small"
            />
        </div>
    );

    return (
        <Card className={style.schedule_container}>
            <div className={style.timezone_row}>
                <div>
                    <ErrorAwareLabel
                        text="Timezone"
                        hasError={checkIfHasError(timezoneField)}
                    />
                    <Tooltip title="All dates and times will be interpreted in this timezone">
                        <InfoCircleOutlined style={{ paddingLeft: '0.4rem' }} />
                    </Tooltip>
                </div>
                <TimezoneSelect
                    value={timezone}
                    onChange={(value) => setFieldValue(timezoneField, value)}
                    placeholder="Select timezone"
                />
            </div>
            <div className={style.schedule_row}>
                {renderDatePicker(
                    'Start date',
                    'startDate',
                    'This is the date when the item becomes visible to users in the app.',
                    startDate,
                    (date) => setFieldValue('startDate', date),
                    (date) =>
                        !!(
                            fileSelected &&
                            (!date || date.isSameOrBefore(moment()))
                        ),
                    false
                )}
                {renderDatePicker(
                    'End date',
                    'endDate',
                    'This is the date when the item will be automatically removed from the app.',
                    endDate,
                    (date) => setFieldValue('endDate', date),
                    (date) =>
                        !!(
                            !date ||
                            (startDate && date.isSameOrBefore(startDate))
                        ),
                    true
                )}
            </div>
            <div className={style.business_units_row}>
                <ErrorAwareLabel
                    text="Region"
                    hasError={checkIfHasError('provinces')}
                />
                <BusinessUnitsSelectTree
                    value={provinces?.map((p) => p.provinceId) || []}
                    onChange={(selectedProvinceIds) => {
                        const updatedProvinces = handleProvinceSelectionChange(
                            selectedProvinceIds,
                            provinces || []
                        );
                        setFieldValue('provinces', updatedProvinces);
                    }}
                />
            </div>
        </Card>
    );
};
